/** @format */

import React ,{ useContext, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit";
import { useTranslation } from "react-i18next";
import { roles } from "../../Config/Roles";
import { AuthContext } from "../../Contexts/AuthContext";
import { GlobalContext } from "../../Contexts/GlobalContextProvider";
import { globalServices } from "../../Services/global.service";
import { supportService } from "../../Services/support.service";
import ActionModal from "../Common/ActionModal";
import ResultModal from "../Common/ResultModal";
import SpinnerLoading from "../Common/SpinnerLoading";
import AddSupport from "./AddSupport";
import UpdateSupport from "./UpdateSupport";

const Index = () => {
  const { t: translator } = useTranslation();
  const { updateLocName } = useContext(GlobalContext);
  const { roleId } = useContext(AuthContext);
  const { master, admin, support } = roles;

  const isActionAllowed = [master, admin, support].includes(parseInt(roleId));

  const { SearchBar } = Search;

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [selectedLanguageId, setSelectedLanguageId] = useState("");
  const [updateData, setUpdateData] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [update, setUpdate] = useState(new Date());

  const [resultModal, setResultModal] = useState({
    show: false,
    title: "",
    text: "",
    btnClass: "",
  });

  const [actionModal, setActionModal] = useState({
    show: false,
    title: "",
    text: "",
    confirmBtn: "",
  });

  useEffect(() => updateLocName("support"), []);

  useEffect(() => {
    globalServices.getLanguages().then((res) => {
      setLanguages(res.data);
      const croatian = res.data.find(
        (l) => l.name.toLowerCase().includes("hrvat") || l.shortName?.toLowerCase() === "hr"
      );
      if (croatian) setSelectedLanguageId(croatian.id);
    });
  }, []);

  useEffect(() => {
    setIsLoading(true);
    supportService
      .getAll(selectedLanguageId ? parseInt(selectedLanguageId) : undefined)
      .then((res) => {
        setData(res.data);
        setIsLoading(false);
      })
      .catch((err) => console.log(err));
  }, [update, selectedLanguageId]);

  const onClickDelete = (rowId) => {
    setActionModal({
      show: true,
      title: "Support-Delete-Title",
      text: "Support-Delete-Text",
      confirmBtn: "Support-Delete-Btn",
      confirmAction: () => confirmDelete(rowId),
    });
  };

  function actionFormatter(rowId) {
    return (
      <div
        className="action"
        onClick={(e) => {
          e.stopPropagation();
          onClickDelete(rowId);
        }}
      >
        <span className="icon icon-cancel" style={{ color: "#c00" }}></span>
      </div>
    );
  }

  const columns = [
    {
      dataField: "supportId",
      text: "",
      headerStyle: () => ({ width: "50px", textAlign: "center" }),
      formatter: actionFormatter,
      hidden: !isActionAllowed,
    },
    {
      dataField: "priority",
      text: translator("Support-Priority"),
      headerStyle: () => ({ width: "80px", textAlign: "center" }),
      style: () => ({ textAlign: "center" }),
    },
    {
      dataField: "title",
      text: translator("Support-Title"),
    },
  ];

  const rowEvents = {
    onClick: (e, row) => {
      e.stopPropagation();
      setUpdateData(row);
      setShowUpdateModal(true);
    },
  };

  const confirmDelete = (id) => {
    supportService
      .remove(id)
      .then(() => {
        setActionModal({ ...actionModal, show: false });
        setResultModal({
          ...resultModal,
          title: translator("Support-ResultModal-DeleteSuccess-Title"),
          text: translator("Support-ResultModal-DeleteSuccess-Text"),
          show: true,
          btnClass: "btn btn-primary",
        });
        setUpdate(new Date());
      })
      .catch(() =>
        setResultModal({
          ...resultModal,
          title: translator("Support-ResultModal-DeleteSuccess-Title"),
          text: translator("Support-ResultModal-DeleteUnSuccess-Text"),
          show: true,
          btnClass: "btn btn-primary",
        })
      );
  };

  return (
    <div className="p-2 pt-3">
      <div className="card container-fluid p-0">
        <div className="card-header card-wrap">
          <span>{translator("Support-View-Subtitle")}</span>
        </div>
        <div className="card-body">
          <ToolkitProvider
            keyField="supportId"
            data={data}
            columns={columns}
            search
          >
            {(props) => (
              <>
                <div className="col-md-12 mt-2">
                  {isLoading ? (
                    <div className="dashboard-card-spinner">
                      <SpinnerLoading />
                    </div>
                  ) : (
                    <>
                      <div className="d-flex justify-content-between align-items-end mb-2">
                        <div>
                          <SearchBar {...props.searchProps} />
                        </div>
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                          <select
                            className="form-control"
                            value={selectedLanguageId}
                            onChange={(e) => setSelectedLanguageId(e.target.value)}
                          >
                            <option value="">{translator("Support-AllLanguages")}</option>
                            {languages.map((lang) => (
                              <option key={lang.id} value={lang.id}>
                                {lang.name}
                              </option>
                            ))}
                          </select>
                          {isActionAllowed && (
                            <button
                              className="btn btn-primary text-nowrap"
                              onClick={() => setShowAddModal(true)}
                            >
                              + {translator("Support-AddNew")}
                            </button>
                          )}
                        </div>
                      </div>
                      <BootstrapTable
                        {...props.baseProps}
                        rowClasses="boostrap-table-row"
                        rowEvents={rowEvents}
                        noDataIndication={translator("No-Data")}
                        pagination={paginationFactory({})}
                      />
                    </>
                  )}
                </div>
              </>
            )}
          </ToolkitProvider>
        </div>
      </div>

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{translator("Support-AddNew")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AddSupport
            onSuccess={() => {
              setUpdate(new Date());
              setShowAddModal(false);
            }}
            disabled={!isActionAllowed}
            setResultModal={setResultModal}
            languages={languages}
          />
        </Modal.Body>
      </Modal>

      <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{translator("Support-Update")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {updateData && (
            <UpdateSupport
              updateData={updateData}
              disabled={!isActionAllowed}
              onSuccess={() => {
                setUpdate(new Date());
                setShowUpdateModal(false);
              }}
              setResultModal={setResultModal}
              languages={languages}
            />
          )}
        </Modal.Body>
      </Modal>

      <ResultModal
        show={resultModal.show}
        title={resultModal.title}
        text={resultModal.text}
        btnClass={resultModal.btnClass}
        handleClose={() => {
          setResultModal({ ...resultModal, show: false });
        }}
      />

      <ActionModal
        show={actionModal.show}
        handleHide={() => {
          setActionModal({ ...actionModal, show: false });
        }}
        title={actionModal.title}
        text={actionModal.text}
        confirmBtn={actionModal.confirmBtn}
        confirmAction={actionModal.confirmAction}
      />
    </div>
  );
};

export default Index;
